
import moment from 'moment'
import { mapGetters } from 'vuex'
import Stepper from './v2/parts/Stepper.vue'
import SlotStrip from './v2/parts/SlotStrip.vue'
import FooterNav from './v2/parts/FooterNav.vue'
import SaveModal from './v2/parts/SaveModal.vue'
import EligibilityStep from './v2/steps/EligibilityStep.vue'
import AboutStep from './v2/steps/AboutStep.vue'
import ConfirmDetailsStep from './v2/steps/ConfirmDetailsStep.vue'
import ScheduleStep from './v2/steps/ScheduleStep.vue'
import GuestsStep from './v2/steps/GuestsStep.vue'
import SoaStep from './v2/steps/SoaStep.vue'
import ConfirmStep from './v2/steps/ConfirmStep.vue'
import { STEPS, buildSessionId } from '~/data/schedule-v2-config.js'
import { fetchOpenStates, isOpenState, openStateMethods, stateNames } from '~/util/open-states'
import { agentTakesTier } from '~/util/advisor-tiers'

// sessionStorage, not localStorage (2026-09-22): a refresh in the same tab
// keeps the attempt (progress + hold), a new tab or a later visit starts a new
// one. With localStorage every attempt in a browser shared one id for ever, and
// each overwrote the previous attempt's saved state and Activity trail.
const SESSION_KEY = 'sch-v2-session-id'

function newGuest () {
    return {
        first: '',
        last: '',
        relationship: '',
        needsGuidance: null,
        returning: null,
        // Per-guest lookup state. Vue 2 only makes properties reactive if they
        // exist when the object is first observed — so EVERY field GuestsStep
        // mutates (g.lookupError, g.lookupLoading, ...) MUST be declared here.
        // If you set a brand-new key on a guest later (e.g. g.lookupError = '...')
        // the template won't update until some *other* declared key changes —
        // that's the "error only shows after I edit the DOB year" bug. Add the
        // key here (or use this.$set), never rely on bare assignment of a new key.
        lookupFound: false,
        lookupError: '',
        lookupLoading: false,
        lookupEmail: '',
        lookupDobM: '',
        lookupDobD: '',
        lookupDobY: '',
        email: '',
        phone: '',
        // Set by the guest lookup (/client-token/ has_phone). A found-returning
        // guest skips the gprofile page, so when this is false GuestsStep must
        // collect a phone before booking (#568).
        hasPhoneOnFile: false,
        sms_opt_in: true,
        dobM: '',
        dobD: '',
        dobY: '',
        gender: null,
        zip_code: '',
        fips: '',
        // #680 Phase 2a: full address, alongside ZIP. `state` was previously
        // undeclared here and only ever set via Location's onLocation $set —
        // AboutDemographicBlock's new explicit state <select> writes it via a
        // plain v-model assignment, so it (like street_1/street_2/city) must
        // exist at observe time for Vue 2 reactivity, same rule as every
        // other field on this factory.
        street_1: '',
        street_2: '',
        city: '',
        state: '',
        medicare_parts: null,
        coverage_urgency: null,
        effective_date: null,
        hsa: false,
        collecting_ssi: false,
        moving: false,
        moving_state: '',
        moving_zipcode: '',
        token: null,
        soa60days: null,
        products: {},
        product_answers: {},
        soaSigned: false,
        soaName: '',
        soaSignedAt: null,
        // 'beneficiary' | 'representative' — who is signing the SOA (#573)
        soaRole: null,
        soaRepName: '',
        // …and their relationship to the beneficiary, for the SOA's rep box (#605)
        soaRepRelationship: '',
    }
}

function newClient (preset) {
    return {
        returning: null,
        lookupFound: false,
        lookupEmail: '',
        lookupDobM: '',
        lookupDobD: '',
        lookupDobY: '',
        connection: null,
        group: null,
        orgVerified: false,
        eligName: '',
        eligEmail: '',
        eligPhone: '',
        eligOrg: '',
        eligSent: false,
        first_name: '',
        last_name: '',
        preferred_name: '',
        email: '',
        phone: '',
        // Whether the account on file already has a phone number. Hydrated by
        // the returning lookup (/client-token/ has_phone) or the prop preset.
        // When false, the eligibility step collects one — returning clients
        // skip the About step, so booking would otherwise go out phoneless (#568).
        hasPhoneOnFile: false,
        sms_opt_in: true,
        // #679: the Step 1 AEP screener answer — 'new_to_medicare' |
        // 'plan_review' — sent to /book/ as `reason`. `enrolled` comes from
        // the /client-token/ returning lookup and drives the pre-select.
        // Vue 2 reactivity — every key mutated later (by the lookup or the
        // reason picker) must exist here at observe time.
        aep_reason: null,
        enrolled: false,
        dob: null,
        dobM: '',
        dobD: '',
        dobY: '',
        gender: null,
        zip_code: '',
        fips: '',
        // #680 Phase 2a: full address, alongside ZIP — see the matching
        // fields in newGuest() above for why `state` needs declaring here too.
        street_1: '',
        street_2: '',
        city: '',
        state: '',
        // #679 Phase 2: whether the lookup echoed the PII read-back fields
        // (only a session signed in as this client, or staff, unlocks them —
        // #686). Drives the confirm step's address row; has_full_address
        // picks G (read-back) vs G2 (required inputs) there.
        addressEchoed: false,
        has_full_address: false,
        group_relationship: null,
        source_type: null,
        source_name: '',
        allowed_methods: [],
        // Whether allowed_methods reflects an authoritative answer (returning
        // lookup, verified group, or slug prefill) vs. the not-yet-known default.
        // Empty allowed_methods only blocks the client once this is true — so a
        // new user mid-flow (or an agent-mode preset) still defaults to all methods.
        allowedMethodsResolved: false,
        // Former Benefit Bay (ICHRA) client, closed 2026-10-01: the returning
        // lookup found the closed account (formerIchra), or the new-client
        // employer search hit a former Benefit Bay group (groupFormerIchra).
        // Two flags because Yes/No toggling keeps both branches' state. Either
        // shows the partnership message and nothing else unlocks.
        formerIchra: false,
        groupFormerIchra: false,
        // #730: Find & Verify found the employer, but it participates at no
        // level. The client's state then decides (FL / RI books directly).
        groupNonPaying: false,
        // Payer / non-payer advisor routing (Dien 2026-10-15): the verified
        // group is paying or financial-advisor (a new client's tier), and the
        // lookup's verdict for a returning client (null until it answers).
        groupPayer: false,
        payer: null,
        // #727: "Who referred you?" -- asked only of a None of the Above
        // visitor in an open state. Sent to /book/ as the primary's `referral`.
        referred_by_type: null,
        referred_by_client_name: '',
        referred_by_employer_name: '',
        referred_by_employer_contact: '',
        referred_by_note: '',
        agent: null,
        agents: [],
        soa60days: null,
        // #576: soonest existing upcoming appointment ({datetime, manage_token})
        // or null, set by the returning lookup. Declared here so the Time step
        // reacts when the eligibility step assigns it (Vue 2 reactivity).
        upcoming_appointment: null,
        token: null,
        medicare_parts: null,
        coverage_urgency: null,
        effective_date: null,
        // #679 Phase 2: new client + AEP plan review — the About step's
        // Medicare-situation half swaps to these (quick-aep vocabulary).
        current_medicare_coverage: null,
        supp_carrier: '',
        supp_name: '',
        pdp_carrier: '',
        pdp_name: '',
        mapd_carrier: '',
        mapd_name: '',
        hsa: false,
        collecting_ssi: false,
        moving: false,
        moving_state: '',
        moving_zipcode: '',
        products: {},
        product_answers: {},
        soaSigned: false,
        soaName: '',
        soaSignedAt: null,
        // 'beneficiary' | 'representative' — who is signing the SOA (#573)
        soaRole: null,
        soaRepName: '',
        // …and their relationship to the beneficiary, for the SOA's rep box (#605)
        soaRepRelationship: '',
        ...(preset || {}),
    }
}

export default {
    components: {
        Stepper,
        SlotStrip,
        FooterNav,
        SaveModal,
        EligibilityStep,
        AboutStep,
        ConfirmDetailsStep,
        ScheduleStep,
        GuestsStep,
        SoaStep,
        ConfirmStep,
    },
    props: {
        client: { type: Object, default: null },
        // Agent pinned via ?agent=<id>. Overrides the looked-up/owning agent and
        // hard-locks every availability query to this agent.
        pinnedAgent: { type: Object, default: null },
        test: { type: Boolean, default: false },
        slug: { type: String, default: null },
        jumpToLastPage: { type: Boolean, default: false },
    },
    data () {
        return {
            sessionId: this.loadOrCreateSessionId(),
            step: 'eligibility',
            saveOpen: false,
            stepError: '',
            heldUntil: null,
            saveDebounce: null,
            state: {
                client: newClient(this.client
                    ? {
                        id: this.client.id || null,
                        first_name: this.client.first_name || '',
                        last_name: this.client.last_name || '',
                        preferred_name: this.client.preferred_name || '',
                        email: this.client.email || '',
                        phone: this.client.cell_phone || this.client.phone || '',
                        hasPhoneOnFile: !!(this.client.cell_phone || this.client.phone || this.client.home_phone),
                        dob: this.client.dob || null,
                        gender: this.client.gender || null,
                        zip_code: this.client.zip_code || '',
                        fips: this.client.fips || '',
                        group: this.client.group?.id || this.client.group || null,
                        group_relationship: this.client.group_relationship || null,
                        source_type: this.client.source_type || null,
                        source_name: this.client.source_name || '',
                        medicare_parts: this.client.medicare_parts || null,
                        effective_date: this.client.effective_date || null,
                        coverage_urgency: this.client.coverage_urgency || null,
                        hsa: !!this.client.hsa,
                        collecting_ssi: !!this.client.collecting_ssi,
                        moving: !!this.client.moving,
                        moving_state: this.client.moving_state || '',
                        moving_zipcode: this.client.moving_zipcode || '',
                        soa60days: this.client.soa60days || null,
                        // #679 Phase 2: a preset record (agent ?id= via
                        // getSurvey) carries the address whenever its
                        // serializer exposes it — the same unlock as the
                        // lookup echo, since the viewer is already staff.
                        street_1: this.client.street_1 || '',
                        street_2: this.client.street_2 || '',
                        city: this.client.city || '',
                        state: this.client.state || '',
                        addressEchoed: this.client.street_1 !== undefined,
                        has_full_address: !!(this.client.street_1 && this.client.city && this.client.state),
                        returning: this.client.id ? true : null,
                        lookupFound: !!this.client.id,
                    }
                    : null),
                method: null,
                date: null,
                time: null,
                agent: null,
                agents: null,
                // Returning client's answer to "work with your current agent
                // again?" (#570): 1 = yes (only that agent), 0 = no (everyone
                // else), -1 = doesn't matter, null = not yet asked/answered.
                keepAgent: null,
                notes: '',
                hasGuests: null,
                guests: [],
            },
            booking: false,
            // Pool of agents resolved from the client's location, used when no
            // agent is pinned (new users). Mirrors what the legacy Book.vue
            // does via getAgentsForLocation.
            resolvedAgentIds: [],
            // #727: states where a None of the Above visitor books directly
            // (the open-states setting, via the public endpoint).
            openStates: [],
            // All schedulable agents (no location filter). Used only to warm the
            // "soonest opening" hint on landing, before we know the client's
            // location. Kept separate from resolvedAgentIds so the Time step
            // never books against this broad pool.
            defaultAgentIds: [],
            // Pre-fetched soonest day for each call method ('YYYY-MM-DD'),
            // so the Time step can show "Soonest opening" instantly.
            soonest: { phone: null, video: null },
            // Signed token returned by /book for a real (non-email) booking;
            // lets the confirmation page deep-link to the /scheduled manage page.
            manageToken: null,
            // Field-level validation gates: flipped on when the user tries to
            // advance past a step that still has missing required fields, so the
            // step's inputs can highlight exactly what's missing.
            gateAbout: false,
            gateSoa: false,
            gateConfirm: false,
        }
    },
    computed: {
        ...mapGetters(['isAEPScheduling']),
        // The AEP season window (Sep 1 – Dec 3). Gates only the things that
        // must stay visible whatever the screener answer is — the screener
        // itself, its enrolled pre-select, and persisting the answer — or a
        // client who picked "I'm new to Medicare" could never change it back.
        aepSeason () { return !!this.isAEPScheduling },
        // The EFFECTIVE flag everything downstream keys off (Aziz's 2026-09-01
        // finding): a client who told us they're new to Medicare is not an
        // AEP plan review, whatever the calendar says — no AEP survey tile,
        // no AEP appointment type (whose Acuity calendars have no openings
        // before Oct 15), video allowed again. Unanswered (null) stays on the
        // season default, matching the never-screened new-client path.
        aep () {
            return this.aepSeason && this.state.client.aep_reason !== 'new_to_medicare'
        },
        // Email and Survey are both "asynchronous" methods: no calendar slot,
        // an advisor follows up by email. They share nav/validation behavior.
        isAsyncMethod () {
            return this.state.method === 'email' || this.state.method === 'survey'
        },
        // The contact methods to offer on the Time step. Once authoritatively
        // resolved we honor the real list (which may be empty → blocked, or a
        // subset like ['email'] / ['survey']); until then we default to all
        // three so a not-yet-resolved flow (e.g. agent-mode preset) isn't blocked.
        resolvedMethods () {
            const c = this.state.client
            if (c.allowedMethodsResolved) return c.allowed_methods || []
            // #679: the server already strips video under AEP at booking time;
            // this fallback covers the window before allowedMethodsResolved
            // lands, so an AEP client is never shown a tile the server will
            // refuse.
            return this.aep ? ['phone', 'email'] : ['phone', 'video', 'email']
        },
        // True once we authoritatively know this client has NO available methods.
        // Drives the eligibility-step block (returning lookup / verified group)
        // and the Time-step fallback block.
        methodsResolvedEmpty () {
            const c = this.state.client
            return !!c.allowedMethodsResolved && !(c.allowed_methods && c.allowed_methods.length)
        },
        // Block the eligibility step right after a returning lookup (or a verified
        // new-client group) comes back with no available methods.
        eligibilityBlocked () {
            if (this.baseStep !== 'eligibility') return false
            const c = this.state.client
            if (!this.methodsResolvedEmpty) return false
            return (c.returning === true && c.lookupFound) ||
                (c.returning === false && c.orgVerified) ||
                (c.returning === false && this.openStateDirect) ||
                (c.returning === false && !!c.groupNonPaying)
        },
        // #727/#730: a non-paying visitor (None of the Above, or a group that
        // does not participate) who lives in an open state books directly
        // (phone, plus the AEP survey in season) -- the same predicate the
        // backend applies at /book/.
        // Payer / non-payer routing: the tier the Calendar sends with /dates/
        // and /times/. A new client is a payer only with a paying or
        // financial-advisor group; a returning client's comes from the lookup.
        availabilityPayer () {
            const c = this.state.client
            if (c.returning === true) return typeof c.payer === 'boolean' ? c.payer : null
            if (c.returning === false) return !!c.groupPayer
            return null
        },
        openStateDirect () {
            const c = this.state.client
            return c.returning === false && (c.connection === 'other' || !!c.groupNonPaying) && isOpenState(c.state, this.openStates)
        },
        // #679: whether to show the Step 1 "what would you like to cover on
        // this call?" screener. Only once identity clears (a returning-found
        // lookup, or a verified new-client group) — same conditions as
        // eligibilityBlocked, but gated on the SEASON (not the effective aep,
        // which the answer itself flips) and NOT on methodsResolvedEmpty
        // being true (that's the opposite, blocked, case). validate() reuses
        // this exact condition so the two can never disagree.
        showScreener () {
            if (!this.aepSeason || this.methodsResolvedEmpty) return false
            const c = this.state.client
            return (c.returning === true && c.lookupFound) ||
                (c.returning === false && c.orgVerified) ||
                (c.returning === false && this.openStateDirect)
        },
        // Live wizard sequence: depends on returning-found + guest guidance.
        sequence () {
            const seq = ['eligibility']
            const isReturning = this.state.client.returning === true && this.state.client.lookupFound
            if (!isReturning) seq.push('about')
            // #679 Phase 2 (panels G/G2): under AEP, returning clients no
            // longer skip Step 2 — they get the confirm-details read-back
            // (issue body overrides mockup panel E's stale "still skipped").
            if (isReturning && this.aep) seq.push('confirm_details')
            seq.push('schedule', 'guests')
            // Per-guest profile pages for guidance-needing guests that aren't found-returning
            this.guestGuidanceIdx().forEach((gi) => {
                const g = this.state.guests[gi]
                if (!(g && g.returning === true && g.lookupFound === true)) {
                    seq.push(`gprofile#${gi}`)
                }
            })
            // SOA (skip if soa60days is fresh)
            if (!this.state.client.soa60days) seq.push('soa')
            // Per-guest SOAs (skip if their soa60days is fresh)
            this.guestGuidanceIdx().forEach((gi) => {
                const g = this.state.guests[gi]
                if (!g.soa60days) seq.push(`gsoa#${gi}`)
            })
            seq.push('confirm')
            return seq
        },
        currentIndex () { return Math.max(0, this.sequence.indexOf(this.step)) },
        // Stepper labels. When the AEP confirm-details step replaces the
        // About step for returning clients (#679 Phase 2, panel G), its
        // stepper slot reads "Confirm" instead of "About you"; every other
        // flow keeps the static STEPS list byte-identical.
        stepLabels () {
            if (this.sequence.indexOf('confirm_details') !== -1) {
                return STEPS.map(s => (s.id === 'about' ? { id: 'confirm_details', label: 'Confirm' } : s))
            }
            return STEPS
        },
        baseStep () { return String(this.step).split('#')[0] },
        guestIdx () {
            const p = String(this.step).split('#')
            return p.length > 1 ? Number(p[1]) : null
        },
        // What to highlight in the stepper (collapses gprofile/gsoa into Guests/Sign)
        progressId () {
            const b = this.baseStep
            if (b === 'gprofile') return 'guests'
            if (b === 'gsoa') return 'soa'
            return b
        },
        showChrome () {
            return this.baseStep !== 'confirm'
        },
        showSlotStrip () {
            const b = this.baseStep
            return ['guests', 'gprofile', 'soa', 'gsoa'].indexOf(b) > -1 &&
                (this.isAsyncMethod || (this.state.date && this.state.time))
        },
        currentStepComponent () {
            const b = this.baseStep
            if (b === 'eligibility') return 'EligibilityStep'
            if (b === 'about') return 'AboutStep'
            if (b === 'confirm_details') return 'ConfirmDetailsStep'
            if (b === 'schedule') return 'ScheduleStep'
            if (b === 'guests') return 'GuestsStep'
            if (b === 'gprofile') return 'AboutStep'
            if (b === 'soa') return 'SoaStep'
            if (b === 'gsoa') return 'SoaStep'
            if (b === 'confirm') return 'ConfirmStep'
            return 'EligibilityStep'
        },
        currentStepProps () {
            const b = this.baseStep
            if (b === 'eligibility') {
                return { client: this.state.client, slug: this.slug, aep: this.aep, showScreener: this.showScreener, openStates: this.openStates }
            }
            if (b === 'about') {
                return {
                    target: this.state.client,
                    isPrimary: true,
                    orgName: this.state.client.source_name,
                    showEmployerRel: this.state.client.connection === 'family' && !!this.state.client.orgVerified,
                    gate: this.gateAbout,
                    // #679 Phase 2: new client booking an AEP plan review —
                    // the Medicare-situation half swaps to current coverage.
                    planReview: this.aep && this.state.client.aep_reason === 'plan_review',
                }
            }
            if (b === 'confirm_details') {
                return { target: this.state.client, gate: this.gateConfirm }
            }
            if (b === 'schedule') {
                return {
                    state: this.state,
                    agentIds: this.effectiveAgentIds,
                    soonest: this.soonest,
                    allowedMethods: this.resolvedMethods,
                    aep: this.aep,
                    reason: this.state.client.aep_reason,
                    sessionId: this.sessionId,
                    payer: this.availabilityPayer,
                    error: this.stepError,
                    pinnedAgent: this.pinnedAgent,
                    showKeepAgent: this.showKeepAgent,
                    returningAgent: this.returningAgent,
                    upcomingAppointment: this.state.client.upcoming_appointment || null,
                }
            }
            if (b === 'guests') {
                return {
                    hasGuests: this.state.hasGuests,
                    guests: this.state.guests,
                    slug: this.slug,
                    aep: this.aep,
                }
            }
            if (b === 'gprofile') {
                return { target: this.state.guests[this.guestIdx], isPrimary: false, gate: this.gateAbout }
            }
            if (b === 'soa') {
                return {
                    target: this.state.client,
                    isPrimary: true,
                    apptTime: this.state.time,
                    apptMethod: this.state.method,
                    gate: !!this.gateSoa,
                    needsAddress: this.soaNeedsAddress(this.state.client),
                }
            }
            if (b === 'gsoa') {
                const ggi = this.guestGuidanceIdx()
                const pos = ggi.indexOf(this.guestIdx) + 1
                return {
                    target: this.state.guests[this.guestIdx],
                    isPrimary: false,
                    guestPos: pos,
                    guestTotal: ggi.length,
                    apptTime: this.state.time,
                    apptMethod: this.state.method,
                    gate: !!this.gateSoa,
                    needsAddress: this.soaNeedsAddress(this.state.guests[this.guestIdx]),
                }
            }
            if (b === 'confirm') {
                return {
                    client: this.state.client,
                    guests: this.state.guests,
                    method: this.state.method,
                    time: this.state.time,
                    manageToken: this.manageToken,
                    // Drives the duration line and the add-to-calendar block:
                    // AEP books the 30-minute type, everything else 45.
                    aep: this.aep,
                }
            }
            return {}
        },
        // The returning client's current/owning agent (object with id +
        // first/last name), set by the /client-token/ lookup. Null for new users.
        returningAgent () {
            const c = this.state.client
            if (c.returning === true && c.lookupFound && c.agent) {
                return c.agent
            }
            return null
        },
        // Eligible agents from the returning client's looked-up location pool.
        eligibleReturningAgents () {
            // Payer / non-payer routing: an advisor who does not meet this
            // client's tier is not offered, and not asked about ("work with
            // Kalena again?" would only lead to an empty calendar). Email and
            // survey bookings still go to the client's own advisor.
            return (this.state.client.agents || []).filter(a => a && a.eligible && agentTakesTier(a, this.availabilityPayer))
        },
        // Whether to ask "would you like to work with {agent} again?" (#570).
        // Mirrors the legacy Book.vue conditions: a returning client whose
        // current agent is one of MORE THAN ONE eligible agents (so there's a
        // real choice), and no ?agent pin (a pin overrides agent selection).
        showKeepAgent () {
            if (this.pinnedAgent) return false
            const prev = this.returningAgent
            if (!prev || !prev.id) return false
            const eligible = this.eligibleReturningAgents
            if (eligible.length < 2) return false
            return eligible.some(a => (a.id || a) === prev.id)
        },
        agentIdsForCalendar () {
            // A ?agent pin overrides everything (including a returning client's
            // looked-up agent) and is the only agent queried for availability.
            if (this.pinnedAgent) {
                return [this.pinnedAgent.id]
            }
            // "Work with your agent again?" narrows the pool (#570): Yes books
            // only the previous agent; No queries the eligible pool minus them.
            // Doesn't matter / unanswered falls through to the whole pool.
            if (this.showKeepAgent) {
                const prevId = this.returningAgent.id
                if (this.state.keepAgent === 1) {
                    return [prevId]
                }
                if (this.state.keepAgent === 0) {
                    return this.eligibleReturningAgents
                        .map(a => a.id || a)
                        .filter(id => id !== prevId)
                }
            }
            // Returning users have their pinned agent + alts from the lookup.
            // Payer / non-payer routing: never query an advisor the tier excludes.
            if (this.state.client.agents && this.state.client.agents.length) {
                return this.state.client.agents.filter(a => agentTakesTier(a, this.availabilityPayer)).map(a => a.id || a)
            }
            if (this.state.client.agent) {
                return [this.state.client.agent.id || this.state.client.agent]
            }
            return []
        },
        // Agent pool the Time step actually queries against: pinned agents
        // when known, otherwise the location-resolved pool (new users have no
        // pinned agent yet, so /dates/ + /times/ would otherwise no-op).
        effectiveAgentIds () {
            const pinned = this.agentIdsForCalendar
            return pinned.length ? pinned : this.resolvedAgentIds
        },
        ctaLabel () {
            const b = this.baseStep
            if (b === 'confirm_details') return 'Confirm and pick a time'
            if (b === 'schedule') return (this.aep && this.state.method === 'survey') ? 'Send me the survey' : (this.isAsyncMethod ? 'Continue' : 'Reserve this time')
            if (b === 'soa' || b === 'gsoa') {
                const tgt = b === 'soa' ? this.state.client : this.state.guests[this.guestIdx]
                if (!tgt?.soaSigned) return 'Sign to continue'
                const isLast = (this.sequence[this.currentIndex + 1] === 'confirm')
                return isLast ? 'Confirm booking' : 'Continue'
            }
            return 'Continue'
        },
        ctaDisabled () {
            return this.booking
        },
        // On the Time step (once a method is picked) the error is shown inline
        // next to the calendar/selection, so don't also surface it down in the
        // footer (which sits below the fold). Other steps keep the footer error.
        footerError () {
            if (this.baseStep === 'schedule' && this.state.method) return ''
            return this.stepError
        },
        // The "None of the Above" connection is a terminal branch -- the user
        // submits an eligibility request to our team and waits for an email,
        // so there's nowhere to continue to -- unless they live in an open
        // state (#727), where they book directly. Hide Continue otherwise.
        hideContinue () {
            // No methods available for this account — there's nowhere to go.
            if (this.eligibilityBlocked) return true
            // #576: returning client already has an upcoming appointment — the
            // Time step offers only Reschedule / Keep, so there's no Continue.
            if (this.baseStep === 'schedule' && this.state.client.upcoming_appointment) return true
            // Defensive: a non-participating account that somehow reached the
            // Time step (eligibility normally catches it) has no method to book.
            if (this.baseStep === 'schedule' && !this.resolvedMethods.length) return true
            const c = this.state.client
            return this.baseStep === 'eligibility' &&
                c.returning === false &&
                c.connection === 'other' &&
                !this.openStateDirect
        },
    },
    mounted () {
        fetchOpenStates(this.$api).then((states) => {
            if (states.length) this.openStates = states
            // A visitor may already have picked None of the Above + a state.
            this.judgeHomeState()
        })
        // Pre-select a call method so the user lands on the Time step with
        // one already chosen. Honor a `?method=phone|video|email` URL param
        // when present; default to "phone" otherwise. tryResume may overwrite
        // this from a saved session, which is the right precedence.
        if (!this.state.method) {
            // #679: video is never valid under AEP (server strips it at
            // booking time) — exclude it here too so ?method=video coerces
            // back to the default 'phone' instead of landing on a method the
            // server will refuse.
            const allowed = this.aep ? ['phone', 'email', 'survey'] : ['phone', 'video', 'email', 'survey']
            const fromUrl = String(this.$route?.query?.method || '').toLowerCase()
            this.state.method = allowed.indexOf(fromUrl) > -1 ? fromUrl : 'phone'
        }
        // If the eligibility step later narrows the allowed methods (e.g. an
        // email-only group), coerce our selection back into the allowed set so
        // the Time step never shows a method whose tile isn't even rendered.
        this.$watch('state.client.allowed_methods', (methods) => {
            if (!methods || !methods.length) return
            if (methods.indexOf(this.state.method) === -1) {
                this.state.method = methods[0]
                // A coerced method re-scopes the availability pool exactly like
                // a clicked one, so any slot already picked (and the advisor
                // picked with it) has to go — see ScheduleStep.setMethod.
                this.state.date = null
                this.state.time = null
                this.state.agent = null
                this.state.agents = null
            }
        })
        // Aziz 2026-09-01: the effective AEP flag flips when the Step 1
        // screener answer changes (an enrolled pre-select corrected to "I'm
        // new to Medicare", or back). Everything fetched under the old flag
        // is stale then — the allowed-methods tier (AEP survey vs video) and
        // the prefetched soonest days (a different appointment type's
        // calendar) — so refetch both. The allowed_methods watcher above
        // then coerces a now-invalid method pick (e.g. 'survey') back into
        // the refreshed set.
        this.$watch('aep', () => {
            this.soonest = { phone: null, video: null }
            this.prefetchSoonest()
            this.reresolveAllowedMethods()
        })
        this.tryResume().then(() => {
            this.applyQueryPreset()
            this.maybePrefillFromSlug()
            this.hydrateReturningAgentPool()
            // Last, so it never overwrites a resume or slug prefill — both are
            // more specific than "whoever happens to be signed in".
            this.resolveMethodsForSignedInClient()
        })
        // Auto-save on most mutations.
        this.$watch('state', () => this.scheduleSave(), { deep: true })
        // Whenever the data needed to resolve an agent pool changes (location
        // for new users, or a pinned agent landing for returning users),
        // pre-fetch the soonest day for both phone and video in parallel so
        // the Time step renders its "Soonest opening" pill instantly.
        this.$watch(
            // #727: openStateDirect narrows the pool to the allow-listed advisors, so
            // it is part of the location key -- the pool refetches when it flips.
            () => `${this.state.client.fips || ''}|${this.state.client.state || ''}|${this.openStateDirect}|${this.agentIdsForCalendar.join(',')}`,
            (next, prev) => {
                const nextParts = String(next).split('|')
                const prevParts = String(prev || '').split('|')
                // If the location key changed, drop the cached location pool.
                const locChanged = nextParts.slice(0, 3).join('|') !== prevParts.slice(0, 3).join('|')
                if (locChanged) {
                    this.resolvedAgentIds = []
                }
                // A location OR agent-pool change invalidates the prefetched
                // soonest: a returning client narrowing to "keep my agent" (#570)
                // must re-pill against only that agent's openings, not the wider
                // pool's. Without this the cached soonest would survive the change.
                const agentsChanged = nextParts[3] !== prevParts[3]
                if (locChanged || agentsChanged) {
                    this.soonest = { phone: null, video: null }
                }
                this.prefetchSoonest()
            },
            { immediate: true },
        )
    },
    beforeDestroy () {
        // Best-effort release if the user closes mid-wizard.
        this.releaseHold(false)
    },
    methods: {
        loadOrCreateSessionId () {
            try {
                const existing = window.sessionStorage.getItem(SESSION_KEY)
                if (existing) return existing
                const id = buildSessionId()
                window.sessionStorage.setItem(SESSION_KEY, id)
                return id
            } catch (e) {
                return buildSessionId()
            }
        },
        // On a fresh /schedule/:slug (V2) load, treat the slug as the employer:
        // pre-pick "new client → my employer" and auto-verify the group, mirroring
        // the 1-page flow's getGroup(slug). Skipped for known/returning clients and
        // for resumed sessions (guarded on a pristine returning/connection so we
        // never clobber a user's own choice or restored state).
        // #727 deep-link / smoke-test presets. Explicit keys only, so the
        // Intercom links that already carry ?source_type= keep their behavior:
        //   ?returning=0|1                 new client / returning client
        //   ?connection=employer|advisor|family|dpc|other
        //   ?state=FL                      the None of the Above state question
        //   ?ref=client|employer|other     who referred (+ ?ref_text= name / employer / note)
        // The page already maps ?email, ?dob and ?source_name onto the client;
        // a returning preset copies email + DOB into the lookup fields. A
        // ?resume token wins: the saved session is more specific than a link.
        applyQueryPreset () {
            const q = (this.$route && this.$route.query) || {}
            if (q.resume) return
            const str = (v) => { const x = Array.isArray(v) ? v[0] : v; return x == null ? '' : String(x).trim() }
            const c = this.state.client
            const returning = str(q.returning).toLowerCase()
            if (['0', 'false', 'no', 'new'].includes(returning)) c.returning = false
            else if (['1', 'true', 'yes'].includes(returning)) c.returning = true
            const connection = str(q.connection).toLowerCase()
            const tile = connection ? (this.$store.getters.connections || []).find(t => t.value === connection) : null
            if (tile) {
                c.returning = false
                c.connection = tile.value
                c.source_type = tile.sourceType
            }
            const state = str(q.state).toUpperCase()
            if (state && c.connection === 'other') c.state = state
            const ref = str(q.ref).toUpperCase()
            if (c.connection === 'other' && ['CLIENT', 'EMPLOYER', 'OTHER'].includes(ref)) {
                c.referred_by_type = ref
                const text = str(q.ref_text)
                if (text) {
                    if (ref === 'CLIENT') c.referred_by_client_name = text
                    else if (ref === 'EMPLOYER') c.referred_by_employer_name = text
                    else c.referred_by_note = text
                }
            }
            if (c.returning === true) {
                if (c.email && !c.lookupEmail) c.lookupEmail = c.email
                const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(c.dob || '')
                if (m && !c.lookupDobY) { c.lookupDobY = m[1]; c.lookupDobM = m[2]; c.lookupDobD = m[3] }
            }
            this.judgeHomeState()
        },
        // #727/#730: the "Which state do you live in?" answer, judged here as
        // well as on the click (EligibilityStep.judgeHomeState): for the URL
        // preset, for a slug landing, and for the open states arriving after
        // the visitor already picked a state.
        judgeHomeState () {
            const c = this.state.client
            if (c.returning !== false || !(c.connection === 'other' || c.groupNonPaying) || !c.state || !this.openStates.length) return
            if (this.openStateDirect) {
                if (c.connection === 'other') c.source_type = 'OTHER'
                c.allowed_methods = openStateMethods(this.aep)
                c.allowedMethodsResolved = true
            } else {
                c.allowed_methods = []
                // A non-participating group in a closed state is blocked (and
                // told); the None of the Above path shows the request form instead.
                c.allowedMethodsResolved = !!c.groupNonPaying
            }
        },
        async maybePrefillFromSlug () {
            const c = this.state.client
            if (!this.slug || c.id || c.returning !== null || c.connection) return
            try {
                const group = await this.$api.getGroup(this.slug)
                if (!group || !group.id) return
                if (group.config && group.config.former_ichra) {
                    // D9 by the employer landing page's Schedule button: a former
                    // Benefit Bay group. Never verified -- the eligibility step
                    // shows the partnership message and nothing unlocks.
                    c.returning = false
                    c.connection = 'employer'
                    c.source_type = 'EMPLOYER'
                    c.source_name = group.name
                    c.groupFormerIchra = true
                    return
                }
                // #730: a group that participates at no level asks the client's
                // state instead of blocking -- the same as Find & Verify.
                if (!(group.paying || group.financial_advisor || group.dpc)) {
                    c.returning = false
                    c.connection = 'employer'
                    c.source_type = 'EMPLOYER'
                    c.group = group.id
                    c.source_name = group.name
                    c.orgVerified = false
                    c.groupNonPaying = true
                    // The landing keeps the employer's branding, as a paying slug does.
                    this.$store.commit('group', group)
                    this.judgeHomeState()
                    return
                }
                // A slug can point at an advisor firm too; derive the connection
                // from the group like the 1-page flow does. Defaults to employer.
                const isAdvisor = !!group.financial_advisor
                c.returning = false
                c.connection = isAdvisor ? 'advisor' : 'employer'
                c.source_type = isAdvisor ? 'FINANCIAL_ADVISOR' : 'EMPLOYER'
                c.group = group.id
                c.source_name = group.name
                c.orgVerified = true
                c.groupPayer = !!(group.paying || group.financial_advisor)
                this.$store.commit('group', group)
                try {
                    const methods = await this.$api.getGroupSchedulingMethods(group.id, { aep: this.aep })
                    if (Array.isArray(methods)) {
                        c.allowed_methods = methods
                        c.allowedMethodsResolved = true
                    }
                } catch (e) { /* fall back to all methods */ }
            } catch (e) {
                // Unknown/invalid slug — fall back to the normal eligibility flow.
            }
        },
        // The third resolver, alongside the returning lookup and the slug
        // prefill. Without it a client who is simply signed in never resolves
        // at all: allowedMethodsResolved stays false, resolvedMethods falls
        // back to all three tiles, and a selfserve client is shown Phone and
        // Video only to be refused by the server at booking time.
        //
        // Skipped once anything more specific has already answered, and for a
        // signed-in agent (the backend returns null for them) — an agent
        // previewing wizard mode must not inherit a client's eligibility, nor
        // be blocked by their own lack of it.
        async resolveMethodsForSignedInClient () {
            const c = this.state.client
            if (c.allowedMethodsResolved) { return }
            if (this.$store?.getters?.user?.role !== 'client') { return }
            try {
                const resp = await this.$api.getMyAllowedMethods({ aep: this.aep })
                if (Array.isArray(resp?.allowed_methods)) {
                    c.allowed_methods = resp.allowed_methods
                    c.allowedMethodsResolved = true
                }
            } catch (e) {
                // Leave it unresolved: the wizard keeps offering all three and
                // the server still refuses a disallowed method at booking.
            }
        },
        // Refetch allowed methods under the CURRENT effective aep flag,
        // through whichever path resolved them originally (see the `aep`
        // watcher in mounted()). No-op until something has resolved them.
        async reresolveAllowedMethods () {
            const c = this.state.client
            if (!c.allowedMethodsResolved) return
            // Returning-found lookup: the token refresh already refetches
            // allowed_methods with `aep: this.aep` and re-hydrates the rest
            // of the lookup payload coherently.
            if (c.token && c.lookupEmail && c.lookupDobY) {
                await this.refreshReturningTokenIfAny()
                return
            }
            // #727/#730: a visitor the open-state rule governs has no group to
            // ask; the rule is phone, plus the AEP survey when the effective aep is on.
            if (this.openStateDirect) {
                c.allowed_methods = openStateMethods(this.aep)
                return
            }
            // Verified new-client group (GroupFind pick or a slug prefill).
            // `group` is an object on the returning path but a bare id here.
            if (c.orgVerified && c.group) {
                try {
                    const methods = await this.$api.getGroupSchedulingMethods(c.group.id || c.group, { aep: this.aep })
                    if (Array.isArray(methods)) {
                        c.allowed_methods = methods
                        c.allowedMethodsResolved = true
                    }
                } catch (e) { /* keep the old list; /book/ still enforces */ }
                return
            }
            // Signed-in client (resolveMethodsForSignedInClient's path).
            if (this.$store?.getters?.user?.role === 'client') {
                try {
                    const resp = await this.$api.getMyAllowedMethods({ aep: this.aep })
                    if (Array.isArray(resp?.allowed_methods)) {
                        c.allowed_methods = resp.allowed_methods
                        c.allowedMethodsResolved = true
                    }
                } catch (e) { /* leave as-is */ }
            }
        },
        async tryResume () {
            const token = this.$route.query.resume
            if (!token) return
            try {
                const resp = await this.$api.$post('/api/scheduling/wizard-state/resume/', { token })
                if (resp?.state) {
                    const saved = { ...resp.state }
                    // The step the user was on is persisted alongside the form
                    // data under _step (see scheduleSave). Pull it out before
                    // merging so it doesn't leak into the state object.
                    const resumeStep = saved._step
                    delete saved._step
                    this.state = { ...this.state, ...saved }
                    this.sessionId = resp.session_id || this.sessionId
                    try { window.sessionStorage.setItem(SESSION_KEY, this.sessionId) } catch (e) { /* ignore */ }
                    // #679: a resumed AEP session with an unanswered Step 1
                    // screener must land back on eligibility, not wherever it
                    // was saved — otherwise a resume link would let a client
                    // skip the screener gate entirely. Snapshotted from the
                    // just-merged (saved) state, before the token refresh below
                    // can pre-select aep_reason — that pre-select is only a
                    // convenience fill-in on the eligibility step, so the user
                    // still confirms it there rather than being carried past it.
                    const screenerUnanswered = this.showScreener && !this.state.client.aep_reason
                    // Refresh the returning-client token so a stale itsdangerous
                    // signature never surfaces as a 422 at book time.
                    const tokenOk = await this.refreshReturningTokenIfAny()
                    // #679 Phase 2: same rule as the screener for the confirm
                    // step — a resumed AEP session must not land past it while
                    // it is still incomplete (phone missing, or an unlocked
                    // address row left unfinished). Checked AFTER the token
                    // refresh, which is what re-hydrates hasPhoneOnFile and
                    // the echo fields.
                    const confirmPending = this.sequence.indexOf('confirm_details') !== -1 && (
                        (!this.state.client.hasPhoneOnFile && !this.state.client.phone) ||
                        (this.state.client.addressEchoed && !(this.state.client.street_1 || '').trim())
                    )
                    // Drop the user back where they left off — but only if the
                    // token refresh didn't force them back to re-verify, the
                    // step is still reachable given the resumed data, and the
                    // screener (if shown) had already been answered.
                    if (tokenOk && resumeStep && this.sequence.indexOf(resumeStep) !== -1 && !screenerUnanswered) {
                        if (confirmPending && this.sequence.indexOf(resumeStep) > this.sequence.indexOf('confirm_details')) {
                            this.step = 'confirm_details'
                        } else {
                            this.step = resumeStep
                        }
                    }
                }
            } catch (err) {
                this.$alert({ message: 'This resume link has expired or is invalid.', variant: 'warning' })
            }
        },
        // Returns true when the token is fine (or there's nothing to refresh),
        // false when we had to clear it and bounce back to eligibility.
        async refreshReturningTokenIfAny () {
            const c = this.state.client
            if (!(c && c.token && c.lookupEmail && c.lookupDobY)) return true
            try {
                const dob = `${c.lookupDobY}-${String(c.lookupDobM).padStart(2, '0')}-${String(c.lookupDobD).padStart(2, '0')}`
                const resp = await this.$api.getExistingClientToken({
                    email: c.lookupEmail, dob, slug: this.slug || undefined, aep: this.aep,
                })
                if (resp?.token) {
                    c.token = resp.token
                    this.$set(c, 'payer', typeof resp.payer === 'boolean' ? resp.payer : null)
                    // Keep allowed methods authoritative on resume — they may have
                    // changed since the session was saved.
                    c.allowed_methods = resp.allowed_methods || []
                    c.allowedMethodsResolved = true
                    // #679: keeps a resumed session's pre-select coherent — a
                    // session saved off-season has enrolled=false stored even
                    // for enrolled clients, so re-hydrate it here too and
                    // re-run the same pre-select rule the eligibility step uses.
                    this.$set(c, 'enrolled', !!resp.enrolled)
                    if (this.aepSeason && resp.enrolled && c.aep_reason == null) {
                        this.$set(c, 'aep_reason', 'plan_review')
                    }
                    // #679 Phase 2: the PII echo, present only when this
                    // session is signed in as the client or staff (#686).
                    // Fill as a coherent block, and never clobber values the
                    // resumed session already edited.
                    this.$set(c, 'addressEchoed', 'street_1' in resp)
                    if ('street_1' in resp) {
                        this.$set(c, 'has_full_address', !!resp.has_full_address)
                        if (!(c.street_1 || '').trim()) {
                            c.street_1 = resp.street_1 || ''
                            c.street_2 = resp.street_2 || ''
                            c.city = resp.city || ''
                            this.$set(c, 'state', resp.state || '')
                        }
                        if (resp.phone && !c.phone) c.phone = resp.phone
                    }
                    return true
                }
                c.token = null
                c.lookupFound = false
                this.step = 'eligibility'
                return false
            } catch (e) {
                c.token = null
                c.lookupFound = false
                this.step = 'eligibility'
                return false
            }
        },
        // #686 (Aziz ruling): whether the SOA step must collect this signer's
        // full address. Returning-found signers whose session never unlocked
        // the on-file echo enter it fresh (nothing on file is ever shown at
        // the email+dob auth bar); logged-in/staff sessions arrive with
        // addressEchoed and new clients answered the About step. Stable
        // inputs only — typing into the block must not collapse it.
        soaNeedsAddress (target) {
            return target.returning === true && target.lookupFound === true && !target.addressEchoed
        },
        guestGuidanceIdx () {
            return (this.state.guests || []).reduce((acc, g, i) => {
                if (g && g.needsGuidance === true) acc.push(i)
                return acc
            }, [])
        },
        go (step) {
            this.stepError = ''
            this.gateSoa = false
            this.gateAbout = false
            this.gateConfirm = false
            this.step = step
            if (typeof window !== 'undefined' && typeof window.scrollTo === 'function') window.scrollTo(0, 0)
        },
        back () {
            const i = this.currentIndex
            if (i > 0) this.go(this.sequence[i - 1])
        },
        async next () {
            const err = this.validate()
            if (err) {
                this.stepError = err
                return
            }
            const i = this.currentIndex
            const nextStep = this.sequence[Math.min(this.sequence.length - 1, i + 1)]
            if (nextStep === 'confirm') {
                await this.book()
                return
            }
            this.go(nextStep)
        },
        validate () {
            const b = this.baseStep
            const c = this.state.client
            if (b === 'eligibility') {
                if (c.returning === null) return 'Let us know if you’ve worked with us before.'
                // No available methods for this account — block (Continue is hidden,
                // so this is a belt-and-suspenders guard).
                if (this.eligibilityBlocked) return 'Doctor’s Choice isn’t available for this account. Please email help@doctorschoiceusa.com.'
                if (c.returning === true && !c.lookupFound) return 'Please look up your account to continue.'
                // Returning clients skip About (the step that requires a phone) —
                // if the account has no phone on file, one must be entered here
                // (#568). Under AEP the confirm-details step owns that job
                // (#679 Phase 2), so don't double-gate it here.
                if (c.returning === true && c.lookupFound && !c.hasPhoneOnFile && !c.phone &&
                    this.sequence.indexOf('confirm_details') === -1) {
                    return 'Please add a phone number so your advisor can reach you.'
                }
                if (c.returning === false) {
                    if (!c.connection) return 'Let us know how you’re connected to Doctor’s Choice.'
                    // #730: a group that does not participate -- the state decides.
                    if (c.connection !== 'other' && c.groupNonPaying) {
                        if (!c.state) return 'Please tell us which state you live in.'
                    } else {
                        if (c.connection === 'dpc' && !c.orgVerified) return 'Please verify your doctor to continue.'
                        if (c.connection !== 'other' && !c.orgVerified) return 'Please verify your employer or advisor to continue.'
                    }
                    if (c.connection === 'other') {
                        if (this.openStateDirect) {
                            if (!c.referred_by_type) return 'Please tell us who referred you to Doctor’s Choice.'
                            if (c.referred_by_type === 'EMPLOYER' && !(c.referred_by_employer_name || '').trim()) return 'Please enter your employer’s name.'
                        } else if (!c.eligSent) {
                            return 'Please submit your details to our team.'
                        }
                    }
                }
                // #679: Step 1 screener — block Continue until the client tells
                // us what to cover, once showScreener resolves true (mirrors
                // EligibilityStep's own gate so the two never disagree).
                if (this.showScreener && !c.aep_reason) return 'Please tell us what you’d like to cover on this call.'
            }
            if (b === 'about' || b === 'gprofile') {
                const isPrimary = b === 'about'
                // #727/#730: a non-paying client books directly only from an
                // open state. Their address can change the state chosen in
                // step 1; catch it here, not at /book/ after the SOA signature.
                if (isPrimary && c.returning === false && (c.connection === 'other' || c.groupNonPaying) && !this.openStateDirect) {
                    return `Doctor’s Choice can only book you directly if you live in ${stateNames(this.openStates)}. Please check your state, or go back to the first step.`
                }
                const t = isPrimary ? c : this.state.guests[this.guestIdx]
                const showEmployerRel = isPrimary && c.connection === 'family' && !!c.orgVerified
                const err = this.aboutError(t, isPrimary, showEmployerRel)
                if (err) { this.gateAbout = true; return err }
            }
            // #679 Phase 2: the confirm-details read-back. Phone is always
            // required; the address only gates when the session unlocked the
            // row (addressEchoed) — the absolute address-to-book requirement
            // is deferred with #686.
            if (b === 'confirm_details') {
                if (!(c.first_name || '').trim() || !(c.last_name || '').trim()) {
                    this.gateConfirm = true
                    return 'Please add your full name.'
                }
                if (!c.phone && !c.hasPhoneOnFile) {
                    this.gateConfirm = true
                    return 'Please add a phone number so your advisor can reach you.'
                }
                if (c.addressEchoed) {
                    if (!(c.street_1 || '').trim()) { this.gateConfirm = true; return 'Please add your street address.' }
                    if (!(c.city || '').trim()) { this.gateConfirm = true; return 'Please add your city.' }
                    if (!c.state) { this.gateConfirm = true; return 'Please choose your state.' }
                    if (!c.fips) { this.gateConfirm = true; return 'Please add a ZIP code so we can match you to plans.' }
                }
            }
            if (b === 'schedule') {
                if (!this.state.method) return 'Pick how you’d like to connect.'
                if (!this.isAsyncMethod) {
                    if (!this.state.date) return 'Please choose a date.'
                    if (!this.state.time) return 'Please choose a time.'
                }
            }
            if (b === 'guests') {
                if (this.state.hasGuests === null) return 'Let us know if anyone else is joining.'
                // Found-returning guests skip the gprofile page (the step that
                // requires a phone) — their account must have one on file or
                // one must be entered on this step (#568).
                const phoneless = (this.state.guests || []).find(g =>
                    g && g.needsGuidance === true && g.returning === true && g.lookupFound === true &&
                    !g.hasPhoneOnFile && !g.phone
                )
                if (phoneless) return `Please add a phone number for ${phoneless.first || 'your guest'}.`
            }
            if (b === 'soa' || b === 'gsoa') {
                const tgt = b === 'soa' ? c : this.state.guests[this.guestIdx]
                // #686 (Aziz): a returning signer whose session never
                // unlocked the on-file echo must enter their full address on
                // this step — never echoed, always updates the file.
                if (this.soaNeedsAddress(tgt)) {
                    const who = b === 'soa' ? 'your' : 'their'
                    if (!(tgt.street_1 || '').trim()) { this.gateSoa = true; return `Please add ${who} street address.` }
                    if (!(tgt.city || '').trim()) { this.gateSoa = true; return `Please add ${who} city.` }
                    if (!tgt.state) { this.gateSoa = true; return `Please choose ${who} state.` }
                }
                // Every product starts ticked on the step (legal, 2026-09-24:
                // every plan checked by default, the client can uncheck), so
                // there is no per-product answer left to demand. The one gate
                // is that they did not untick them all. An old "unsure" saved
                // in wizard state before the deploy still counts as in scope.
                const answers = tgt.product_answers || {}
                const products = ['mapd', 'pdp', 'supp', 'dental_vision_hearing']
                if (!products.some(p => answers[p] === 'yes' || answers[p] === 'unsure')) {
                    this.gateSoa = true
                    return 'Please tick at least one product to discuss.'
                }
                if (!tgt.soaRole) {
                    this.gateSoa = true
                    return 'Please tell us who is signing — the beneficiary or an authorized representative.'
                }
                if (tgt.soaRole === 'representative' && !(tgt.soaRepName || '').trim()) {
                    return 'Please add the authorized representative’s full name.'
                }
                if (tgt.soaRole === 'representative' && !(tgt.soaRepRelationship || '').trim()) {
                    return 'Please add the representative’s relationship to the beneficiary.'
                }
                if (!tgt.soaSigned) {
                    return b === 'soa'
                        ? 'Please add the signature to continue.'
                        : 'This guest still needs to add their signature.'
                }
            }
            return ''
        },
        // Required-field rules for the About / guest-profile step. Returns the
        // first missing-field message (used for the footer), or '' when valid.
        // The only optional fields are the "Anything special we should know?"
        // checkboxes, the SMS opt-in, and — for guests only — the ZIP code
        // (which defaults to the primary's at book time).
        aboutError (t, isPrimary, showEmployerRel) {
            const who = isPrimary ? 'your' : 'their'
            const them = isPrimary ? 'you' : 'they'
            const first = isPrimary ? t.first_name : t.first
            const last = isPrimary ? t.last_name : t.last
            if (!first) return `Please add ${who} first name.`
            if (!last) return `Please add ${who} last name.`
            if (showEmployerRel && !t.group_relationship) return `Please select ${who} relationship to the employer.`
            if (!t.email) return `Please add ${who} email address.`
            if (!t.phone) return `Please add ${who} phone number.`
            if (!(t.dobM && t.dobD && String(t.dobY || '').length === 4)) return `Please add ${who} date of birth.`
            if (!t.gender) return `Please choose ${who} birth gender.`
            if (isPrimary && !t.fips) return 'Please add your ZIP code.'
            // #680 Phase 2a: full address, required for the primary only —
            // guests keep today's optionality and inherit the primary's at
            // book time (newClientPayload/soaForBookPayload below), same as
            // ZIP. Returning clients skip this step entirely (About isn't in
            // their sequence), so this never gates the confirm/backfill path.
            // Fix 5 (final-review wave): trimmed, like the admin gates
            // (logic.js's client_fields gate) — a lone space must not pass.
            if (isPrimary && !(t.street_1 || '').trim()) return 'Please add your street address.'
            if (isPrimary && !(t.city || '').trim()) return 'Please add your city.'
            if (isPrimary && !t.state) return 'Please choose your state.'
            // #679 Phase 2: the primary booking an AEP plan review answers
            // current coverage instead of parts/start date (the swapped
            // MedicareSituation half). Guests keep the standard questions.
            if (isPrimary && this.aep && this.state.client.aep_reason === 'plan_review') {
                if (!t.current_medicare_coverage) return 'Please tell us what coverage you have today.'
                return ''
            }
            if (!t.coverage_urgency) return `Please tell us when ${them} need coverage to start.`
            if (t.coverage_urgency === 'EXACT' && !t.effective_date) return 'Please pick which month coverage should start.'
            if (!t.medicare_parts) return `Please tell us ${who} current Medicare parts.`
            return ''
        },
        onChange () { this.stepError = '' },
        // #570: a preset/logged-in returning client lands with their account
        // already known (returning + lookupFound) but WITHOUT the owning agent
        // and eligible pool — the EligibilityStep's lookup never runs for them.
        // Fetch the same /client-token/ data the manual lookup uses (matched on
        // email + dob, with the zip pulled from their record server-side) so we
        // can offer "work with your current agent again?". Mirrors the legacy
        // Book.vue flow, which hydrates agent/agents the same way for returning
        // clients.
        async hydrateReturningAgentPool () {
            const c = this.state.client
            if (c.returning !== true || !c.lookupFound) return
            if (c.agents && c.agents.length) return // already hydrated (manual lookup / resume)
            if (!c.email || !c.dob) return // can't match without these
            try {
                const resp = await this.$api.getExistingClientToken({
                    email: c.email,
                    dob: c.dob,
                    slug: this.slug || undefined,
                    aep: this.aep,
                })
                if (!resp || resp.error || resp.detail) return
                if (resp.agent) this.$set(c, 'agent', resp.agent)
                if (resp.agents) this.$set(c, 'agents', resp.agents)
                if (typeof resp.payer === 'boolean') this.$set(c, 'payer', resp.payer)
                // Aziz QA 2026-09-01: a logged-in (preset) client's survey
                // payload carries no soa60days or upcoming appointment, so the
                // wizard re-asked for an SOA signed minutes earlier. This
                // lookup response has both — hydrate them (never clobbering
                // values the preset already knew) so the SOA step skips and
                // the duplicate-booking warning works for signed-in clients
                // exactly like looked-up ones.
                if (resp.soa60days && !c.soa60days) this.$set(c, 'soa60days', resp.soa60days)
                if (resp.upcoming_appointment && !c.upcoming_appointment) {
                    this.$set(c, 'upcoming_appointment', resp.upcoming_appointment)
                }
            } catch (e) {
                // Non-fatal: without the pool we simply don't show the question.
            }
        },
        onUpdateSchedule (next) {
            // Schedule mutations from the step
            Object.assign(this.state, next)
        },
        // Resolve a pool of agents to query for availability. For pinned
        // (returning) clients we already have one; for new users we ask the
        // backend which agents serve their location. Result is cached on
        // `resolvedAgentIds` so we only hit the API once per location.
        async resolveAgentIds () {
            // Returning clients have a pinned agent.
            if (this.agentIdsForCalendar.length) return this.agentIdsForCalendar
            const state = this.state.client.state
            const fips = this.state.client.fips
            // Location known → agents licensed for that location (also the pool
            // the Time step books against, via effectiveAgentIds).
            if (state || fips) {
                if (this.resolvedAgentIds.length) return this.resolvedAgentIds
                try {
                    // #727/#730: open_state_rule asks for the allow-listed advisors only.
                    const resp = await this.$api.getAgentsForLocation({ state, fips, open_state_rule: this.openStateDirect || undefined })
                    this.resolvedAgentIds = (resp?.agents || []).map(a => a.id || a)
                    return this.resolvedAgentIds
                } catch (e) {
                    return []
                }
            }
            // Landing, no location yet → all schedulable agents, just to warm
            // the soonest-opening hint. Refined once the client enters a ZIP.
            if (this.defaultAgentIds.length) return this.defaultAgentIds
            try {
                const resp = await this.$api.getAgentsForLocation({})
                this.defaultAgentIds = (resp?.agents || []).map(a => a.id || a)
                return this.defaultAgentIds
            } catch (e) {
                return []
            }
        },
        // An email or survey booking has no slot to take an advisor from, and a
        // new client has no owner or lookup pool: pick one from the location
        // pool, the way the admin scheduler does (#727 final review). For an
        // open-state Other client that pool is the allow-list. /book/ re-checks.
        poolAgentId () {
            const pool = this.effectiveAgentIds
            return pool.length ? pool[Math.floor(Math.random() * pool.length)] : undefined
        },
        // Pre-warm "Soonest opening" for both phone and video so the Time
        // step shows the pill instantly the moment the user picks a method.
        // Tries the current month, then the next one — at month-end the
        // current month often has nothing left within the 48h booking lead.
        async prefetchSoonest () {
            if (this.soonest.phone && this.soonest.video) return
            const agentIds = await this.resolveAgentIds()
            if (!agentIds.length) return
            const now = moment()
            // /dates/ rejects non-Oct–Dec months outright under aep ("AEP
            // months are October, November, and December"), and AEP
            // scheduling opens Sep 1 — so during the pre-season probe from
            // October, and never past December.
            let start = now
            if (this.aep && now.month() < 9) {
                start = moment(new Date(now.year(), 9, 1))
            }
            const months = [start.format('YYYY-MM'), start.clone().add(1, 'month').format('YYYY-MM')]
                .filter(m => !this.aep || ['10', '11', '12'].indexOf(m.slice(5)) > -1)
            const tz = (typeof Intl !== 'undefined' && Intl.DateTimeFormat().resolvedOptions().timeZone) || 'America/New_York'
            const fetchOne = async (method) => {
                if (this.soonest[method]) return
                // Video is never offered under aep — don't burn /dates/
                // calls probing a tile that can't render.
                if (this.aep && method === 'video') return
                for (const month of months) {
                    try {
                        const resp = await this.$api.$post('/api/scheduling/dates/', {
                            agents: agentIds,
                            month,
                            timezone: tz,
                            method,
                            aep: this.aep,
                            session_id: this.sessionId,
                        })
                        const dates = (resp && resp.dates) || []
                        if (dates.length) {
                            this.$set(this.soonest, method, dates[0])
                            return
                        }
                    } catch (e) { /* try the next month */ }
                }
            }
            await Promise.all([fetchOne('phone'), fetchOne('video')])
        },
        // Returns true if the hold was placed, false on conflict (409).
        async placeHold (slot) {
            if (!slot || !slot.agent || !slot.time) return false
            // Clear any prior "slot taken" error now that they're trying again.
            this.stepError = ''
            // Release any prior hold first
            await this.releaseHold(false)
            try {
                const resp = await this.$api.$post('/api/scheduling/holds/', {
                    agent_id: slot.agent,
                    time: slot.time,
                    session_id: this.sessionId,
                })
                this.heldUntil = (resp.expires_at || 0) * 1000
                return true
            } catch (err) {
                this.stepError = (err && err.message)
                    ? err.message
                    : 'That time was just taken by another visitor. Please pick a different one.'
                this.state.time = null
                return false
            }
        },
        async releaseHold (clearState = true) {
            if (!this.state.time || !this.state.agent) {
                this.heldUntil = null
                return
            }
            const agentId = this.state.agent
            const slotIso = this.state.time
            try {
                await this.$api.$post('/api/scheduling/holds/release/', {
                    agent_id: agentId,
                    time: slotIso,
                    session_id: this.sessionId,
                })
            } catch (e) { /* best-effort */ }
            this.heldUntil = null
            if (clearState) {
                this.state.time = null
                this.state.agent = null
            }
        },
        async onHoldExpired () {
            // The 15-minute hold lapsed. Try to silently re-acquire the same
            // slot — if nobody else grabbed it, the user keeps their time and
            // isn't interrupted. Only bounce them back to pick again if it's
            // now held by someone else.
            this.heldUntil = null
            if (!this.state.time || !this.state.agent) return
            const reacquired = await this.placeHold({
                agent: this.state.agent,
                time: this.state.time,
                agents: this.state.agents,
            })
            if (!reacquired) {
                // placeHold already cleared state.time + set stepError; surface
                // it on the schedule step (go() resets stepError, so re-set after).
                this.go('schedule')
                this.stepError = 'Someone just grabbed that time. Please pick another.'
            }
        },
        addGuest () { this.state.guests.push(newGuest()) },
        removeGuest (i) {
            this.state.guests.splice(i, 1)
            if (!this.state.guests.length) this.state.hasGuests = false
        },
        openSave () {
            this.scheduleSave(true)
            this.saveOpen = true
        },
        scheduleSave (immediate = false) {
            if (this.saveDebounce) clearTimeout(this.saveDebounce)
            // Persist the current step alongside the form data (under _step) so
            // resume drops the user back where they left off.
            const payloadState = { ...this.state, _step: this.step }
            const run = async () => {
                try {
                    await this.$api.$post('/api/scheduling/wizard-state/', {
                        session_id: this.sessionId,
                        state: payloadState,
                    })
                } catch (e) { /* silent — save is best-effort */ }
            }
            if (immediate) return run()
            this.saveDebounce = setTimeout(run, 800)
        },
        async book () {
            this.booking = true
            this.stepError = ''
            try {
                // Build the BookForm payload
                const c = this.state.client
                let clientPayload
                if (c.token) {
                    clientPayload = { token: c.token, phone: c.phone || null, soa: this.soaForBookPayload(c) }
                } else if (c.id) {
                    // Agent-mode booking on behalf of a known client (came in via ?id=).
                    clientPayload = { id: c.id, phone: c.phone || null }
                } else {
                    clientPayload = this.newClientPayload(c)
                }
                // #679 Phase 2: confirm-step NAME corrections ride the
                // corrections whitelist on both returning shapes (token and
                // agent-mode id), only when the confirm step was actually in
                // the flow. #686: ADDRESS corrections persist whenever a
                // returning payload carries a street — the confirm-step echo
                // edit AND the SOA step's fresh entry both land here. Axios
                // drops undefined keys, so a returning payload with no edits
                // stays byte-identical; unchanged values are no-op persists
                // server-side.
                if (c.token || c.id) {
                    if (this.sequence.indexOf('confirm_details') !== -1) {
                        clientPayload.first_name = (c.first_name || '').trim() || undefined
                        clientPayload.last_name = (c.last_name || '').trim() || undefined
                    }
                    if ((c.street_1 || '').trim()) {
                        clientPayload.street_1 = c.street_1
                        clientPayload.street_2 = c.street_2 || null
                        clientPayload.city = c.city || null
                        clientPayload.state = c.state || null
                        clientPayload.zip_code = c.zip_code || undefined
                        clientPayload.fips = c.fips || undefined
                    }
                }

                const guestPayloads = (this.state.guests || []).map((g) => {
                    if (g.needsGuidance) {
                        if (!g.token) return this.newClientPayload(g, true)
                        const gp = { token: g.token, phone: g.phone || null, soa: this.soaForBookPayload(g, true) }
                        // #686: a returning guest's SOA-step address entry
                        // persists exactly like the primary's above.
                        if ((g.street_1 || '').trim()) {
                            gp.street_1 = g.street_1
                            gp.street_2 = g.street_2 || null
                            gp.city = g.city || null
                            gp.state = g.state || null
                        }
                        return gp
                    }
                    // Non-guidance guest → caretaker (won't sign SOA / get survey)
                    return null
                })

                const caretakers = (this.state.guests || []).filter(g => !g.needsGuidance).map(g => ({
                    first_name: g.first,
                    last_name: g.last,
                    email: g.email || null,
                    phone: g.phone || null,
                    relationship: g.relationship || null,
                }))

                const clients = [clientPayload].concat(guestPayloads.filter(Boolean))

                const payload = {
                    clients,
                    caretakers,
                    agent: this.state.agent || this.pinnedAgent?.id || (c.agent?.id || c.agent) || (c.agents && c.agents[0]?.id) || this.poolAgentId(),
                    method: this.state.method,
                    time: this.state.time || null,
                    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York',
                    aep: this.aep,
                    // #679: the Step 1 screener answer, persisted whenever the
                    // season asked it — including "new to Medicare", which
                    // books as a normal (aep: false) consultation. `undefined`
                    // keys are dropped by axios' JSON serialization, so an
                    // off-season payload stays byte-identical to today.
                    reason: this.aepSeason ? (c.aep_reason || null) : undefined,
                    notes: this.state.notes || '',
                    session_id: this.sessionId,
                    // The wizard's "worked with us before?" answer (true/false,
                    // null if never asked — e.g. agent-mode ?id= bookings).
                    returning: c.returning,
                }
                const resp = await this.$api.$post('/api/scheduling/book/', payload)
                this.manageToken = resp?.manage_token || null
                // Per-attempt session id: the booked attempt keeps its id in
                // memory (confirm step, manage link), but the persisted copy goes
                // so the next attempt in this tab gets its own id instead of
                // overwriting this one's saved state and Activity trail.
                try { window.sessionStorage.removeItem(SESSION_KEY) } catch (e) { /* ignore */ }
                this.go('confirm')
            } catch (err) {
                this.stepError = err?.message || 'Could not book your consultation. Please try again.'
            } finally {
                this.booking = false
            }
        },
        soaForBookPayload (target, isGuest = false) {
            if (!target.soaSigned) return null
            // full_name is always the beneficiary; typed_name is whoever signed
            // (the beneficiary, or their authorized representative — #573).
            const beneficiary = ((target.first_name || target.first || '') + ' ' + (target.last_name || target.last || '')).trim()
            const address = this.resolveSoaAddress(target, isGuest)
            const payload = {
                full_name: beneficiary || target.soaName,
                phone: target.phone || null,
                role: target.soaRole || 'beneficiary',
                typed_name: target.soaName,
                products: target.products || {},
                product_answers: target.product_answers || {},
            }
            if (address) payload.address = address
            // The representative's printed name + relationship fill the SOA's
            // authorized-representative section (#605 — see
            // flow.consults.soa.soa_authorized_representative).
            if (target.soaRole === 'representative') {
                payload.representative_name = (target.soaRepName || '').trim()
                payload.representative_relationship = (target.soaRepRelationship || '').trim()
            }
            return payload
        },
        // #680 final-review wave (Fix 1, CRITICAL): the SOA address must come
        // from ONE coherent source, never a partial per-field join. The old
        // per-field join ([street1, city, state, zip].filter(Boolean).join())
        // let a returning wizard client hydrated with ONLY a zip (the lookup
        // never returns street — PII posture) produce a lone zip (e.g.
        // '02886'), which is truthy → payload.address = '02886' → the
        // server's `value.get('address') or user.address`
        // (flow/consults/soa.py:266) PREFERS that bare zip over the client's
        // richer on-file address — a regression of the exact #680 artifact.
        // The same per-field join could also pair a guest's own zip with the
        // primary's street. So: use the target's own address block whole
        // when they have a street of their own; otherwise (guests only) fall
        // back to the primary's whole block; otherwise omit the key
        // entirely so the server's user.address fallback renders whatever IS
        // on file.
        resolveSoaAddress (target, isGuest) {
            if ((target.street_1 || '').trim()) {
                return [target.street_1, target.city, target.state, target.zip_code].filter(Boolean).join(', ')
            }
            if (isGuest) {
                const primary = this.state.client
                if ((primary.street_1 || '').trim()) {
                    return [primary.street_1, primary.city, primary.state, primary.zip_code].filter(Boolean).join(', ')
                }
            }
            return ''
        },
        newClientPayload (target, isGuest = false) {
            const m = String(target.dobM || '').padStart(2, '0')
            const d = String(target.dobD || '').padStart(2, '0')
            const y = target.dobY
            const dob = y ? `${y}-${m}-${d}` : (target.dob || null)
            // A guest who didn't enter a ZIP inherits the primary's location.
            // ZIP/fips stay per-field (unchanged quoting semantics: a guest
            // can share the primary's street while keeping their own ZIP).
            const zipCode = target.zip_code || (isGuest ? this.state.client.zip_code : '') || null
            const resolvedFips = target.fips || (isGuest ? this.state.client.fips : '') || null
            // #680 final-review wave (Fix 2): street_1/street_2/city/state
            // inherit as a coherent BLOCK, not per-field like zip/fips above.
            // A guest who entered no street of their own inherits the
            // primary's whole four; one who entered their own street keeps
            // their own four untouched — never mixed field-by-field (e.g.
            // their own city paired with the primary's street), because a
            // half-inherited address is factually wrong on the persisted
            // User + SOA.
            const guestHasOwnStreet = !!(target.street_1 || '').trim()
            const addressSource = (isGuest && !guestHasOwnStreet) ? this.state.client : target
            const resolvedStreet1 = addressSource.street_1 || null
            const resolvedStreet2 = addressSource.street_2 || null
            const resolvedCity = addressSource.city || null
            const resolvedState = addressSource.state || null
            const entry = {
                first_name: target.first_name || target.first,
                last_name: target.last_name || target.last,
                preferred_name: target.preferred_name || null,
                email: target.email,
                phone: target.phone || null,
                sms_opt_in: target.sms_opt_in !== false,
                dob,
                gender: target.gender,
                zip_code: zipCode,
                fips: resolvedFips,
                street_1: resolvedStreet1,
                street_2: resolvedStreet2,
                city: resolvedCity,
                state: resolvedState,
                group: target.group?.id || target.group || this.state.client.group?.id || this.state.client.group || null,
                group_relationship: target.group_relationship || null,
                source_type: target.source_type || this.state.client.source_type || null,
                source_name: target.source_name || this.state.client.source_name || null,
                medicare_parts: target.medicare_parts,
                coverage_urgency: target.coverage_urgency,
                effective_date: target.effective_date,
                // #679 Phase 2: only the plan-review About swap fills these —
                // axios drops undefined keys, so every other new-client
                // payload stays byte-identical.
                current_medicare_coverage: target.current_medicare_coverage || undefined,
                supp_carrier: (target.supp_carrier || '').trim() || undefined,
                supp_name: (target.supp_name || '').trim() || undefined,
                pdp_carrier: (target.pdp_carrier || '').trim() || undefined,
                pdp_name: (target.pdp_name || '').trim() || undefined,
                mapd_carrier: (target.mapd_carrier || '').trim() || undefined,
                mapd_name: (target.mapd_name || '').trim() || undefined,
                hsa: !!target.hsa,
                collecting_ssi: !!target.collecting_ssi,
                moving: !!target.moving,
                moving_state: target.moving_state || null,
                moving_zipcode: target.moving_zipcode || null,
                relationship: isGuest ? (target.relationship || null) : undefined,
                soa: this.soaForBookPayload(target, isGuest),
            }
            // #727: the primary of an open-state Other booking carries who referred
            // them; guests inherit source_type/state through the fallbacks above
            // but never the referral.
            if (!isGuest && this.openStateDirect && target.connection === 'other') {
                const t = target.referred_by_type
                entry.source_type = 'OTHER'
                entry.referral = {
                    type: t,
                    client_name: t === 'CLIENT' ? ((target.referred_by_client_name || '').trim() || null) : null,
                    employer_name: t === 'EMPLOYER' ? ((target.referred_by_employer_name || '').trim() || null) : null,
                    employer_contact: t === 'EMPLOYER' ? ((target.referred_by_employer_contact || '').trim() || null) : null,
                    // A visitor naming an employer on this path has, by definition,
                    // not verified a participating group -- the ticket flags those.
                    sales_followup: t === 'EMPLOYER',
                    note: t === 'OTHER' ? ((target.referred_by_note || '').trim() || null) : null,
                }
            }
            return entry
        },
        restart () {
            this.state = {
                client: newClient(),
                method: null,
                date: null,
                time: null,
                agent: null,
                agents: null,
                keepAgent: null,
                notes: '',
                hasGuests: null,
                guests: [],
            }
            this.heldUntil = null
            try { window.sessionStorage.removeItem(SESSION_KEY) } catch (e) { /* ignore */ }
            this.sessionId = this.loadOrCreateSessionId()
            this.go('eligibility')
        },
        // #576: the returning client chose to move their existing appointment.
        // Send them to the token-scoped manage page with the reschedule calendar
        // pre-opened (?reschedule=true).
        onRescheduleExisting () {
            const token = this.state.client.upcoming_appointment && this.state.client.upcoming_appointment.manage_token
            if (!token) return
            this.$router.push({ path: '/scheduled', query: { token, reschedule: 'true' } })
        },
        // #576: the returning client chose to keep their existing time. Re-send
        // the confirmation + survey (no Slack, client only), then land on the
        // manage page with a "resent" banner.
        async onKeepCurrentTime () {
            const token = this.state.client.upcoming_appointment && this.state.client.upcoming_appointment.manage_token
            if (!token) return
            // Best-effort resend — even if it fails, still land the client on the
            // manage page so they can see/confirm their existing appointment.
            try { await this.$api.resendScheduledAppointment(token) } catch (e) { /* ignore */ }
            this.$router.push({ path: '/scheduled', query: { token, resent: '1' } })
        },
    },
}
